import { nextTick, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';
import { toast } from 'vue-sonner';
import type { Approval } from '~/types/models';
import type { ClientOverviewAttentionAction } from '~/composables/clients/useClientWorkspaceOverview';

export function useClientWorkspaceAttentionActions(options: {
    dialogState: MaybeRefOrGetter<any>;
    relatedApprovals: MaybeRefOrGetter<Approval[]>;
    activeTab: Ref<string>;
    selectedCardId: Ref<string | null>;
    attentionDialogOpen: Ref<boolean>;
    notesDialogOpen: Ref<boolean>;
    onboardingDialogOpen: Ref<boolean>;
    clientSettingsOpen: Ref<boolean>;
    approvalDialogOpen: Ref<boolean>;
    selectedApproval: Ref<Approval | null>;
    approvalsStore: any;
    openTask: (taskId: string) => void;
}) {
    const approvalSaving = ref(false);

    function openAttentionDialog(cardId: string) {
        options.selectedCardId.value = cardId;
        options.attentionDialogOpen.value = true;
    }

    function itemAction(itemId: string, key: 'primaryAction' | 'secondaryAction') {
        const item = toValue(options.dialogState)?.items.find((entry: any) => entry.id === itemId);
        const action = item?.[key] as ClientOverviewAttentionAction | undefined;
        if (action) void run(action);
    }

    function handlePrimary(itemId: string) {
        itemAction(itemId, 'primaryAction');
    }

    function handleSecondary(itemId: string) {
        itemAction(itemId, 'secondaryAction');
    }

    function handleApprovalRespond(itemId: string) {
        itemAction(itemId, 'primaryAction');
    }

    async function focusTab(tab: string) {
        options.activeTab.value = tab;
        await nextTick();
    }

    async function remindApproval(itemId: string) {
        const item = toValue(options.dialogState)?.items.find((entry: any) => entry.id === itemId);
        const approvalId = item?.approval?.id;
        const projectId = item?.approval?.projectId;
        if (!approvalId || !projectId) {
            toast.error('Approval not found.');
            return;
        }
        try {
            await options.approvalsStore.sendApprovalReminder(approvalId, projectId);
            toast.success('Approval reminder sent.');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to send approval reminder.');
        }
    }

    function openApproval(approval: Approval) {
        options.selectedApproval.value = approval;
        options.approvalDialogOpen.value = true;
    }

    async function run(action: ClientOverviewAttentionAction) {
        if (action.kind === 'task' && action.taskId) {
            options.attentionDialogOpen.value = false;
            options.openTask(action.taskId);
            return;
        }
        if (action.kind === 'approval' && action.approvalId) {
            const approval = toValue(options.relatedApprovals).find(item => item.id === action.approvalId) || null;
            if (!approval) {
                toast.error('Approval not found.');
                return;
            }
            options.attentionDialogOpen.value = false;
            openApproval(approval);
            return;
        }
        if (action.kind === 'href' && action.href) {
            options.attentionDialogOpen.value = false;
            await navigateTo(action.href);
            return;
        }
        if (action.kind === 'tab' && action.tab) {
            options.attentionDialogOpen.value = false;
            await focusTab(action.tab);
            return;
        }
        options.attentionDialogOpen.value = false;
        if (action.dialog === 'notes') options.notesDialogOpen.value = true;
        else if (action.dialog === 'onboarding') options.onboardingDialogOpen.value = true;
        else options.clientSettingsOpen.value = true;
    }

    async function saveApproval(payload: { approval: Approval; status: 'pending' | 'approved' | 'rejected'; note: string; message: string }) {
        approvalSaving.value = true;
        try {
            await options.approvalsStore.respondToApproval(payload.approval.id, payload.approval.projectId, payload.status, payload.note, payload.message);
            options.selectedApproval.value = (options.approvalsStore.approvalsByProject[payload.approval.projectId] || [])
                .find((item: Approval) => item.id === payload.approval.id) || payload.approval;
            options.approvalDialogOpen.value = false;
            toast.success(payload.status === payload.approval.status
                ? 'Approval updated.'
                : payload.approval.status === 'pending'
                    ? payload.status === 'approved' ? 'Approval approved.' : 'Approval rejected.'
                    : 'Approval response updated.');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to save approval response.');
        } finally {
            approvalSaving.value = false;
        }
    }

    watch(options.attentionDialogOpen, (open) => {
        if (!open) options.selectedCardId.value = null;
    });

    return {
        approvalSaving,
        openAttentionDialog,
        handlePrimary,
        handleSecondary,
        handleApprovalRespond,
        focusTab,
        remindApproval,
        saveApproval,
    };
}
